import React, { useState, useEffect, useRef } from "react";
import { GET } from "Common/api";
import InputLabelDatePicker from "Components/Fragment/InputLabelDatePicker";
import Dropzone from "Components/Fragment/Dropzone";
import Form, { Label } from "lib/components/Form";
import { IconButton } from "lib/components/Button";
import PersonalGoalsDropdown from "lib/components/PersonalGoalsDropdown";
import { openExternalURL } from "lib/utils";
import PriorityDropdown from "lib/components/PriorityDropdown";
import { Grid } from "Components/Base/Layout/Grid";
import { Box } from "Components/Base/Layout/Box";
import { Button } from "Components/Base/Inputs/Button";
import ValidationSummary from "lib/components/ValidationSummary";
import SettingStore from "stores/settings";
import withLanguageContext from "lib/hocs/withLanguageContext";
import { Input } from "Components/Base/Inputs/Input";
import * as constants from "./constants";
import * as styles from "./styles";

const AddOtherActivity = ({
  languageContext,
  activity = { title: "" },
  fromEducationHistory,
  isProgramCommonActivity,
  personalId,
  fromProgramPage,
  personalName,
  isManager,
  allowAttachment,
  onCancel,
  save,
  error,
}) => {
  const [activityCategories, setActivityCategories] = useState([]);
  const [ready, setReady] = useState(false);
  const [allowedExtensions, setAllowedExtensions] = useState([]);
  const [isBt, setIsBt] = useState(false);
  const [files, setFiles] = useState(null);
  const [removeAttachment, setRemoveAttachment] = useState(false);

  const [validation, setValidation] = useState({
    msgs: [],
    elements: [],
  });

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    comment: "",
    responsible: "",
    link: "",
    linkLabel: "",
    isCompleted: false,
    allowUserToCompleteActivity: false,
    category: "0",
    personalGoalId: null,
    expireDate: null,
    startDate: "",
    endDate: "",
  });

  const SelPrioRef = useRef(null);

  useEffect(() => {
    const fetchActivityCategories = async () => {
      const categories = await GET(constants.ACTIVITY_CATEGORIES_API);
      setActivityCategories(categories);
    };
    fetchActivityCategories();
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      const isBtValue = SettingStore.getSettingValue("bt") === "1";
      const extensions = await GET("/v1/allowedextensions");

      let expireDateValue = activity.deadlineDate || "";
      if (expireDateValue && expireDateValue.length > 10) {
        [expireDateValue] = expireDateValue.split("T");
      }

      setFormData((prev) => ({
        ...prev,
        title: activity.title || "",
        description: activity.description || "",
        comment: activity.comments || "",
        link: activity.link || "",
        linkLabel: activity.linkLabel || "",
        isCompleted: activity.isCompleted || false,
        responsible: activity.responsible || personalName,
        allowUserToCompleteActivity:
          activity.allowUserToCompleteActivity || false,
        category: activity.activityCategoryId || "0",
        personalGoalId: activity.personalGoalId || activity.goalId,
        expireDate: expireDateValue || "",
        startDate: activity.startDate || "",
        endDate: activity.endDate || "",
      }));
      setReady(true);
      setAllowedExtensions(extensions);
      setIsBt(isBtValue || false);
    };

    initializeData();
  }, [activity, personalName]);

  const errors = error ? { file: { msg: error } } : {};

  const extOk = (fileName) => {
    const extPos = fileName.lastIndexOf(".");
    const ext = fileName.substr(extPos + 1);
    const foundExt = allowedExtensions.find((e) => {
      return e === ext.toLowerCase();
    });
    return foundExt;
  };

  const onDrop = (droppedFiles) => {
    setFiles(droppedFiles);
    setRemoveAttachment(false);
  };

  const getAllowUserToCompleteActivityFormValue = () => {
    const isNewActivity = !activity.id;

    if (isManager && formData.allowUserToCompleteActivity) {
      return true;
    }
    if (!isManager) {
      if (isNewActivity || activity.allowUserToCompleteActivity) {
        return true;
      }
    }
    return false;
  };

  const validateActivity = () => {
    const errorMsgs = [];
    const errorElements = validation.elements.slice(0);

    if (!formData.title) {
      errorMsgs.push({ msg: languageContext("errEnterTitle") });

      if (!errorElements.includes("title")) {
        errorElements.push("title");
      } else {
        errorElements.forEach((e, i) => {
          if (e === "title") {
            errorElements.splice(i, 1);
          }
        });
      }
    }

    if (!fromEducationHistory && !formData.expireDate) {
      errorMsgs.push({ msg: languageContext("enterExpiredate") });

      if (!errorElements.includes("expiredate")) {
        errorElements.push("expiredate");
      } else {
        errorElements.forEach((e, i) => {
          if (e === "expiredate") {
            errorElements.splice(i, 1);
          }
        });
      }
    }

    if (formData.startDate && formData.endDate) {
      if (formData.startDate > formData.endDate) {
        errorMsgs.push({ msg: languageContext("badduration") });
      }
    }

    if (formData.startDate && formData.expireDate) {
      if (formData.startDate > formData.expireDate) {
        errorMsgs.push({ msg: languageContext("badexpiredate") });
      }
    }

    if (files) {
      if (!extOk(files[0].name)) {
        errorMsgs.push({ msg: languageContext("extnotallowed") });
      }
    }

    return { errorMsgs, errorElements };
  };

  const buildActivityData = () => {
    let data = {};
    const activityTitle = isProgramCommonActivity
      ? activity.title
      : formData.title;
    const description = isProgramCommonActivity
      ? activity.description
      : formData.description;

    if (fromEducationHistory) {
      data = {
        personalId,
        attachmentId: activity.attachmentId,
        title: activityTitle,
        comments: formData.comment || "-",
        isCompleted: true,
        createdBy: personalId,
        description,
        startDate: formData.startDate,
        endDate: formData.endDate,
        isOtherTraining: true,
        removeAttachment,
        isExternalCourse: false,
      };
    } else {
      data = {
        personalId,
        attachmentId: activity.attachmentId,
        compTalkId: null,
        courseObjectId: null,
        title: activityTitle,
        comments: formData.comment || "-",
        isCompleted: formData.isCompleted,
        createdBy: personalId,
        period: "-",
        priority: SelPrioRef.current
          ? SelPrioRef.current.state.currentValue
          : null,
        description,
        showComment: false,
        startDate: formData.startDate,
        endDate: formData.endDate,
        isOtherTraining: true,
        courseId: null,
        deadlineDate: formData.expireDate,
        personalGoalId: formData.personalGoalId,
        responsible: formData.responsible,
        link: formData.link,
        linkLabel: formData.linkLabel,
        removeAttachment,
        allowUserToCompleteActivity: getAllowUserToCompleteActivityFormValue(),
        isExternalCourse: false,
      };
    }

    if (activityCategories.length > 0) {
      data.category = formData.category;
    }

    return data;
  };

  const SaveActivity = () => {
    const { errorMsgs, errorElements } = validateActivity();

    if (errorMsgs.length === 0) {
      const data = buildActivityData();
      save(data, files);
    } else {
      const dialogContent = document.getElementById("dialog-content");
      if (dialogContent) {
        dialogContent.scrollTop = 0;
      }
    }

    setValidation((prev) => ({
      ...prev,
      msgs: errorMsgs,
      elements: errorElements,
    }));
  };

  const removeFile = () => {
    if (confirm(languageContext("confirmdeletefile"))) {
      setRemoveAttachment(true);
      setFiles(null);
    }
  };

  const renderSelectedFiles = () => {
    let selectedFiles = <div />;

    if (files) {
      selectedFiles = files.map((f) => {
        return (
          <div key={f.name} className="dropzone-selectedfiles-file">
            {f.name}
          </div>
        );
      });
    } else if (activity.attachmentId && !removeAttachment) {
      selectedFiles = (
        <div key={activity.fileName} className="dropzone-selectedfiles-file">
          <a
            href={`utils/StreamFile.aspx?type=goalactivity&param=${activity.id}&attachmentid=${activity.attachmentId}`}
            className="nowrap"
            target="_blank"
            rel="noreferrer noopener"
          >
            {activity.fileName}
          </a>
          &nbsp;
          <span
            className="fa fa-external-link"
            aria-hidden="true"
            title={languageContext("openinnewwindow")}
          />
        </div>
      );
    }
    return selectedFiles;
  };

  const renderCommonActivityFiles = () => {
    let selectedFiles = <div />;

    if (activity.commonActivityAttachmentID && !removeAttachment) {
      selectedFiles = (
        <div
          key={activity.commonActivityFileName}
          className="dropzone-selectedfiles-file"
        >
          <a
            href={
              "utils/StreamFile.aspx?type=commonActivity&param=" +
              activity.commonActivityId +
              "&param2=" +
              activity.personalPlannedCoursesId +
              "&attachmentid=" +
              activity.commonActivityAttachmentID
            }
            className="nowrap"
            target="_blank"
            rel="noreferrer noopener"
          >
            {activity.commonActivityFileName}
          </a>
          &nbsp;
          <span
            className="fa fa-external-link"
            aria-hidden="true"
            title={languageContext("openinnewwindow")}
          />
        </div>
      );
    }
    return selectedFiles;
  };

  const doRenderActivityCategories = (categories) => {
    return (
      <div>
        <label htmlFor="addactivityCategory" className="aligntop">
          {languageContext("category")}:
        </label>
        <select
          name="addactivityCategory"
          id="addactivityCategory"
          className="flex-1"
          aria-label={languageContext("category")}
          value={formData.category}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, category: e.target.value }))
          }
        >
          <option value="0">- {languageContext("choosecategory")} - </option>
          {categories.map((category, index) => (
            <option value={category.activityCategoryId} key={index}>
              {languageContext(category.title)}
            </option>
          ))}
        </select>
      </div>
    );
  };

  if (!ready) return <div />;

  const newActivity = !activity.hasOwnProperty("id");
  const HideCompletedCheckbox =
    fromEducationHistory ||
    (!activity.allowUserToCompleteActivity && !isManager) ||
    newActivity;

  const renderActivityCategories = activityCategories.length > 0 || false;

  const programMyLearningViewShowLink = !isBt && activity.link;

  return (
    <Form onSubmit={() => SaveActivity()}>
      <div id="addactivity" role="group" aria-labelledby="dialog-title">
        {validation.msgs.length > 0 && (
          <ValidationSummary errors={validation.msgs} />
        )}
        <div className="input-box">
          <Label
            htmlFor="inputAddactivity-title"
            className="required"
            label={`${languageContext("title")}:`}
          />
          {isProgramCommonActivity ? (
            <p id="inputAddactivity-title">{activity.title}</p>
          ) : (
            <Input
              id="inputAddactivity-title"
              placeholder=" "
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, title: e.target.value }))
              }
              required
              error={validation.elements.includes("title")}
            />
          )}
        </div>
        {renderActivityCategories &&
          !fromProgramPage &&
          !isBt &&
          doRenderActivityCategories(activityCategories)}
        <div>
          <label htmlFor="addactivity-description" className="aligntop">
            {languageContext("description")}:
          </label>
          {isProgramCommonActivity ? (
            <p id="addactivity-description" name="addactivity-description">
              {activity.description}
            </p>
          ) : (
            <textarea
              id="addactivity-description"
              className="small"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
            />
          )}
          {activity.commonActivityAttachmentID && (
            <p>
              <label className="aligntop">{languageContext("file")}</label>
              <div className="flex">
                <div>{renderCommonActivityFiles()}</div>
              </div>
            </p>
          )}
        </div>
        {!fromEducationHistory && !fromProgramPage && !isBt && (
          <div>
            <label htmlFor="addactivity-priority">
              {`${languageContext("priority")}:`}
            </label>
            <PriorityDropdown
              id="addactivity-priority"
              defaultValue={activity.priority}
              ref={SelPrioRef}
            />
          </div>
        )}

        {!fromProgramPage && (
          <Box display="flex" sx={{ gap: "16px", flexWrap: "wrap" }}>
            <InputLabelDatePicker
              id="CalStartDate"
              label={languageContext("startdate")}
              name={languageContext("startdate")}
              value={formData.startDate}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, startDate: e.target.value }))
              }
              oldStyleLabel={true}
            />

            <InputLabelDatePicker
              id="CalEndDate"
              label={languageContext("enddate")}
              name={languageContext("enddate")}
              value={formData.endDate}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, endDate: e.target.value }))
              }
              oldStyleLabel={true}
            />
          </Box>
        )}
        {!fromEducationHistory && (
          <div className="flex flex-wrap column">
            <InputLabelDatePicker
              required
              id="CalDate"
              label={languageContext("expiredate")}
              name={languageContext("expiredate")}
              value={formData.expireDate}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, expireDate: e.target.value }))
              }
              oldStyleLabel={true}
            />
          </div>
        )}

        {!fromEducationHistory && activity.formalCompetencies && isBt && (
          <div>
            <label>
              {isBt
                ? `${languageContext("subgoals")}:`
                : `${languageContext("formalCompetencies")}:`}
            </label>
            <styles.StyledFormalCompetenceList>
              {activity.formalCompetencies &&
                activity.formalCompetencies
                  .split(constants.DEVELOPMENT_PLAN_FILTER_SPLIT)
                  .map((formalComptence) => <li>{formalComptence}</li>)}
            </styles.StyledFormalCompetenceList>
          </div>
        )}
        {!fromEducationHistory && !fromProgramPage && (
          <div>
            <label htmlFor="addactivity-connecttogoal">
              {isBt
                ? `${languageContext("program")}:`
                : `${languageContext("connecttogoal")}:`}
            </label>
            <PersonalGoalsDropdown
              id="addactivity-connecttogoal"
              personalId={personalId}
              defaultValue={
                activity.personalGoalId || activity.goalId
              }
              updateParentState={(newValue) =>
                setFormData((prev) => ({ ...prev, personalGoalId: newValue }))
              }
            />
          </div>
        )}
        {!fromEducationHistory && !isBt && (
          <div>
            <label htmlFor="addactivity-responsible">
              {`${languageContext("responsible1")}:`}
            </label>
            {isProgramCommonActivity ? (
              <p id="addactivity-responsible" name="addactivity-responsible">
                {activity.responsible}
              </p>
            ) : (
              <input
                id="addactivity-responsible"
                type="text"
                className="input"
                value={formData.responsible}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    responsible: e.target.value,
                  }))
                }
              />
            )}
          </div>
        )}
        {!fromEducationHistory && activity.formalCompetencies && !isBt && (
          <div>
            <label>
              {isBt
                ? `${languageContext("subgoals")}:`
                : `${languageContext("formalCompetencies")}:`}
            </label>
            <styles.StyledFormalCompetenceList>
              {activity.formalCompetencies &&
                activity.formalCompetencies
                  .split(constants.DEVELOPMENT_PLAN_FILTER_SPLIT)
                  .map((formalComptence) => <li>{formalComptence}</li>)}
            </styles.StyledFormalCompetenceList>
          </div>
        )}
        {programMyLearningViewShowLink && (
          <div className="flex flex-wrap">
            <div>
              <label htmlFor="addactivity-link">
                {`${languageContext("html-editor-link")}:`}
              </label>
              <div className="flex align-c">
                {fromProgramPage ? (
                  <p>
                    {activity.linkLabel || activity.link || ""}
                  </p>
                ) : (
                  <input
                    id="addactivity-link"
                    type="text"
                    className="input"
                    value={formData.link}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, link: e.target.value }))
                    }
                  />
                )}
                <IconButton
                  onClick={() => {
                    const url =
                      formData.link || activity?.link;

                    if (url && url.trim() !== "") {
                      openExternalURL(url);
                    }
                  }}
                  icon="icon-link"
                  label={languageContext("openlink")}
                />
              </div>
            </div>
            {!fromProgramPage && (
              <div>
                <label htmlFor="activity-link-label">
                  {`${languageContext("linkLabel")}:`}
                </label>
                <input
                  id="activity-link-label"
                  type="text"
                  className="input"
                  value={formData.linkLabel}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      linkLabel: e.target.value,
                    }))
                  }
                />
              </div>
            )}
          </div>
        )}
        {!fromEducationHistory && <div></div>}
        {allowAttachment && !isBt && (
          <div>
            <label htmlFor="dropzoneInput">
              {languageContext("document") +
                " (" +
                languageContext("maxfilesize").toLowerCase() +
                ": " +
                SettingStore.getSettingValue("activityAttachmentMaxSize") +
                " MB):"}
            </label>
            <div className="flex">
              <span>{languageContext("file")}: &nbsp;</span>
              <div
                className={
                  files || activity.attachmentId
                    ? "dropzone-selectedfiles"
                    : "hidden"
                }
              >
                <div>{renderSelectedFiles()}</div>
              </div>
              {(files || activity.attachmentId) && !removeAttachment && (
                <span
                  className="pointer"
                  style={{ position: "relative", top: "-10px" }}
                  role="button"
                  title={languageContext("delete")}
                  onClick={removeFile}
                >
                  <i
                    className="iconbutton-icon icon-trash"
                    aria-hidden="true"
                    tabIndex="0"
                  />
                </span>
              )}
            </div>

            {errors.file && (
              <div className="color-error">
                {languageContext(errors.file.msg).replace(
                  "%maxfilesize%",
                  SettingStore.getSettingValue("activityAttachmentMaxSize") +
                    " MB",
                )}
              </div>
            )}
            <Dropzone
              label={
                files
                  ? languageContext("dropzoneinfo2")
                  : languageContext("dropzoneinfo")
              }
              onDrop={onDrop}
            />
          </div>
        )}
        <div>
          <label htmlFor="addactivity-comment">
            {languageContext("comment")}:
          </label>
          <textarea
            id="addactivity-comment"
            className="small"
            value={formData.comment}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, comment: e.target.value }))
            }
          />
        </div>
        {isManager && (
          <div className="flex align-c">
            <label htmlFor="ChkAllowUserToCompleteActivity">
              {languageContext("allowUserToCompleteActivity")}:
            </label>
            <input
              type="checkbox"
              id="ChkAllowUserToCompleteActivity"
              checked={formData.allowUserToCompleteActivity}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  allowUserToCompleteActivity: e.target.checked,
                }))
              }
            />
          </div>
        )}
        <div className={HideCompletedCheckbox ? "hidden" : "flex align-c"}>
          <label htmlFor="ChkCompleted">
            {languageContext("activitycompleted")}:
          </label>
          <input
            type="checkbox"
            id="ChkCompleted"
            checked={formData.isCompleted}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                isCompleted: e.target.checked,
              }))
            }
          />
        </div>
        <Grid container spacing={1} justifyContent="flex-end">
          <Grid item>
            <Button
              color="secondary"
              variant="outlined"
              onClick={onCancel}
              label={languageContext("cancel")}
            />
          </Grid>
          {!activity.isLocked && (
            <Grid item>
              <Button type="submit" label={languageContext("save")} />
            </Grid>
          )}
        </Grid>
      </div>
    </Form>
  );
};

export default withLanguageContext(AddOtherActivity, "TalentManagement");
